/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { UC_BFF_PATHS } from './uc-bff-paths';
import { SESSION_USERKEY, SESSION_USER_INFO } from './constants';
import {
  readAuthorizationFromCookie,
  readTenantIdFromCookie,
} from './authorization-cookie';

const SKIP_TENANT_ID_PATH_FRAGMENTS = [
  UC_BFF_PATHS.login,
  UC_BFF_PATHS.bridge,
  UC_BFF_PATHS.loginAccountInfo,
  '/login/pwd',
  '/loginAccount/loginAccountByPhone',
  '/uc/login/pwd',
  '/uc/login/code',
  '/uc/login/setCookie',
  '/uc/tenant/verifiableCode',
  '/uc/bas/tenant/getforloginuser',
];

export function resolveTenantIdFromUserInfo(
  userInfo: Record<string, unknown>,
): string | undefined {
  const candidates = [
    userInfo.tenantId,
    userInfo.tenant_id,
    (userInfo.userInfo as Record<string, unknown> | undefined)?.tenantId,
    (userInfo.userInfo as Record<string, unknown> | undefined)?.tenant_id,
    (userInfo.user as Record<string, unknown> | undefined)?.tenantId,
    (userInfo.user as Record<string, unknown> | undefined)?.tenant_id,
    userInfo.currentTenantId,
  ];
  for (const tid of candidates) {
    if (tid !== null && tid !== undefined && tid !== '') {
      return String(tid);
    }
  }
  return undefined;
}

function shouldAttachTenantId(url: string): boolean {
  return !SKIP_TENANT_ID_PATH_FRAGMENTS.some(frag => url.includes(frag));
}

/** 从 Cookie / sessionStorage 读取 UC 鉴权头（Authorization + TenantId） */
export function getSessionAuthHeaders(fullUrl: string): Record<string, string> {
  const headers: Record<string, string> = {};
  const cookieAuth = readAuthorizationFromCookie().value;
  if (cookieAuth) {
    headers.Authorization = cookieAuth;
  } else {
    try {
      const token = sessionStorage.getItem(SESSION_USERKEY)?.trim();
      if (token) {
        headers.Authorization = token;
      }
    } catch (error) {
      void error;
    }
  }
  if (!shouldAttachTenantId(fullUrl)) {
    return headers;
  }
  try {
    const raw = sessionStorage.getItem(SESSION_USER_INFO);
    if (raw) {
      const userInfo = JSON.parse(raw) as Record<string, unknown>;
      const tenantId = resolveTenantIdFromUserInfo(userInfo);
      if (tenantId) {
        headers.TenantId = tenantId;
      }
    }
  } catch (error) {
    void error;
  }
  if (!headers.TenantId) {
    const tenantId = readTenantIdFromCookie();
    if (tenantId) {
      headers.TenantId = tenantId;
    }
  }
  return headers;
}
